import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaFileExcel, FaListUl, FaSpinner, FaTable, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { SearchableSelect, SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import SurveyWorkflowSummary from './components/SurveyWorkflowSummary';
import { dateRangeError, dateRangeInputBounds } from '../../utils/date-range.utils';

interface SurveyWorkflowRow {
  [key: string]: any;
}

interface StageOption {
  value: string;
  label: string;
  sequence: number;
}

interface DepartmentOption {
  id: number;
  name: string;
}

interface SurveyWorkflowFilters {
  workflow_stage: string;
  stage_match: 'reached' | 'current';
  project_id: string;
  user_id: string;
  department_id: string;
  activity_id: string;
  role_id: string;
  stage_date_from: string;
  stage_date_to: string;
  /** CSV of status buckets, or 'all' — see COMPLETION_STATUS_OPTIONS. */
  completion_status: string;
  site_id: string;
  search: string;
  sort_by: string;
  sort_order: 'ASC' | 'DESC';
}

/* Work Completion Status — a multi-select over `status` on the site.
 *
 * `status` carries twelve values, but nine of them are waypoints on the way
 * through the workflow (engineer assigned, TA done, report done, …). Users
 * think in four buckets, so the filter offers four and the server folds the
 * waypoints into "In Progress". The buckets are therefore exhaustive: every
 * site matches exactly one, so "All" really is the sum of the four and nothing
 * can hide between the options.
 *
 * The value travels as a CSV string ('in_progress,completed') so the whole
 * filter object stays plain strings; 'all' is the no-filter answer. The default
 * is In Progress + Complete, which is the old "Without Cancelled & Hold Sites"
 * behaviour expressed in the new buckets.
 */
const COMPLETION_STATUS_ALL = 'all';

const COMPLETION_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'hold', label: 'Hold' },
];

const parseCompletionStatus = (value: string): string[] =>
  String(value || '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

/** True when the selection means "every status" — explicitly, or by covering them all. */
const isAllCompletionStatuses = (value: string): boolean => {
  const tokens = parseCompletionStatus(value);
  if (tokens.length === 0 || tokens.includes(COMPLETION_STATUS_ALL)) return true;
  return COMPLETION_STATUS_OPTIONS.every((option) => tokens.includes(option.value));
};

/** Label for the closed dropdown. */
const completionStatusSummary = (value: string): string => {
  if (isAllCompletionStatuses(value)) return 'All';
  const tokens = parseCompletionStatus(value);
  const picked = COMPLETION_STATUS_OPTIONS.filter((option) => tokens.includes(option.value));
  return picked.length > 0 ? picked.map((option) => option.label).join(', ') : 'All';
};

/**
 * The Work Completion Status dropdown: a checkbox list rather than a `<select>`,
 * because several statuses can be reported on at once.
 *
 * "All" and the individual buckets are mutually exclusive on screen — ticking a
 * bucket while All is on narrows to just that bucket, and clearing the last
 * bucket falls back to All, so the filter is never sent empty.
 */
const CompletionStatusFilter: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const tokens = parseCompletionStatus(value);
  const allSelected = isAllCompletionStatuses(value);

  const toggle = (option: string) => {
    const next = allSelected
      ? [option]
      : tokens.includes(option)
        ? tokens.filter((token) => token !== option)
        : [...tokens.filter((token) => token !== COMPLETION_STATUS_ALL), option];
    // Every bucket — or none at all — is just "All".
    if (next.length === 0 || next.length === COMPLETION_STATUS_OPTIONS.length) {
      onChange(COMPLETION_STATUS_ALL);
      return;
    }
    onChange(next.join(','));
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-600"
      >
        <span className="truncate text-gray-900">{completionStatusSummary(value)}</span>
        <FaChevronDown
          className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg py-1">
          <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-blue-50 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onChange(COMPLETION_STATUS_ALL)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="font-medium">All</span>
          </label>
          <div className="border-t border-gray-100 my-1" />
          {COMPLETION_STATUS_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-blue-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={!allSelected && tokens.includes(option.value)}
                onChange={() => toggle(option.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const DEFAULT_FILTERS: SurveyWorkflowFilters = {
  workflow_stage: '',
  // Defaults to "currently at this stage": the question the list is opened to
  // answer is almost always "what is sitting here now", not "what ever passed
  // through here".
  stage_match: 'current',
  project_id: '',
  user_id: '',
  department_id: '',
  activity_id: '',
  role_id: '',
  stage_date_from: '',
  stage_date_to: '',
  completion_status: 'in_progress,completed',
  site_id: '',
  search: '',
  sort_by: 'created_at',
  sort_order: 'DESC',
};

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const EMPTY_PAGINATION: NormalizedPagination = {
  total_items: 0,
  current_page: 1,
  total_pages: 1,
  items_per_page: 50,
  has_next_page: false,
  has_previous_page: false,
};

/* A full-project export streams for as long as it takes to walk every site,
 * which is well past the axios instance's shared 2-minute timeout. */
const EXPORT_TIMEOUT_MS = 30 * 60 * 1000;

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(Math.round(bytes / 1024), 1)} KB`;

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created At' },
  { value: 'updated_at', label: 'Updated At' },
  { value: 'site_id', label: 'Site ID' },
  { value: 'site_name', label: 'Site Name' },
  { value: 'client_allocation_date', label: 'Client Allocation Date' },
  { value: 'site_engineer_assign_date', label: 'Engineer Assign Date' },
  { value: 'site_engineer_work_date', label: 'Site Engineer Work Date' },
];

/** Columns shown in the on-screen grid. The Excel export carries every field. */
const TABLE_COLUMNS: Array<{ key: string; label: string; type?: 'date' | 'bool' }> = [
  { key: 'site_id', label: 'Site ID' },
  { key: 'site_name', label: 'Site Name' },
  { key: 'project_name', label: 'Project' },
  { key: 'activity_name', label: 'Activity' },
  { key: 'state', label: 'State' },
  { key: 'district', label: 'District' },
  { key: 'stage_entered_at', label: 'Stage Entered On', type: 'date' },
  { key: 'stage_performed_by', label: 'Stage Performed By' },
  { key: 'stage_remarks', label: 'Stage Remarks' },
  { key: 'stage_entry_count', label: 'Stage Entries' },
  { key: 'stage_is_current', label: 'Currently At Stage', type: 'bool' },
  { key: 'days_in_stage', label: 'Days In Stage' },
  { key: 'current_status_label', label: 'Current Status' },
  { key: 'client_allocation_date', label: 'Client Allocation', type: 'date' },
  { key: 'site_engineer', label: 'Site Engineer' },
  { key: 'site_engineer_assign_date', label: 'Engineer Assigned', type: 'date' },
  { key: 'field_work_start_date', label: 'Field Work Start', type: 'date' },
  { key: 'field_work_completed_date', label: 'Field Work Completed', type: 'date' },
  { key: 'field_work_duration_days', label: 'Field Work Days' },
  { key: 'site_engineer_work_status', label: 'Field Work Status' },
  { key: 'ta_name', label: 'TA' },
  { key: 'ta_review_completed_at', label: 'TA Review Completed', type: 'date' },
  { key: 'mis_name', label: 'MIS' },
  { key: 'report_designer_name', label: 'Report Designer' },
  { key: 'report_completed_at', label: 'Report Completed', type: 'date' },
  { key: 'validator_name', label: 'Validator' },
  { key: 'validation_completed_at', label: 'Validation Completed', type: 'date' },
  { key: 'report_sent_to_client_at', label: 'Sent To Client', type: 'date' },
  { key: 'uploaded_files_count', label: 'Files' },
  { key: 'uploaded_size', label: 'Data Size' },
  { key: 'rejection_count', label: 'Rejections' },
  { key: 'total_tat_days', label: 'Total TAT (Days)' },
];

const formatDateTime = (value: unknown): string => {
  if (!value) return 'NA';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const display = (value: unknown, type?: 'date' | 'bool'): string => {
  if (type === 'date') return formatDateTime(value);
  if (type === 'bool') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'NA';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/** Everything the detail drawer shows, grouped the way the workflow runs. */
const DETAIL_SECTIONS: Array<{ title: string; fields: Array<[string, string, ('date' | 'bool')?]> }> = [
  {
    title: 'Site',
    fields: [
      ['site_id', 'Site ID'],
      ['site_name', 'Site Name'],
      ['reference_number', 'Reference Number'],
      ['project_name', 'Project'],
      ['activity_name', 'Activity'],
      ['client_project_name', 'Client Project'],
      ['state', 'State'],
      ['district', 'District'],
      ['site_address', 'Address'],
      ['pincode', 'Pincode'],
      ['opco_name', 'OPCO Name'],
      ['opco_id', 'OPCO ID'],
      ['site_priority', 'Priority'],
      ['site_status', 'Site Status'],
      ['site_latitude', 'Latitude'],
      ['site_longitude', 'Longitude'],
      ['customer_requirement', 'Customer Requirement'],
    ],
  },
  {
    title: 'Selected Stage',
    fields: [
      ['stage_label', 'Stage'],
      ['stage_is_current', 'Currently At Stage', 'bool'],
      ['stage_first_entered_at', 'First Entered On', 'date'],
      ['stage_entered_at', 'Last Entered On', 'date'],
      ['stage_entry_count', 'Times Entered'],
      ['stage_from_status_label', 'Moved From'],
      ['stage_action_type', 'Action Type'],
      ['stage_performed_by', 'Performed By'],
      ['stage_performed_by_code', 'Performed By Code'],
      ['stage_remarks', 'Remarks'],
      ['stage_location_verified', 'Location Verified', 'bool'],
      ['days_in_stage', 'Days In Stage'],
      ['days_from_allocation_to_stage', 'Days Allocation → Stage'],
      ['days_from_field_work_to_stage', 'Days Field Work → Stage'],
      ['current_status_label', 'Current Status'],
    ],
  },
  {
    title: 'Allocation & PO',
    fields: [
      ['client_allocation_date', 'Client Allocation Date', 'date'],
      ['pr_number', 'PR Number'],
      ['pr_date', 'PR Date', 'date'],
      ['po_number', 'PO Number'],
      ['po_date', 'PO Date', 'date'],
      ['is_po_shared_at_allocation', 'PO Shared At Allocation', 'bool'],
      ['vendor_po_not_required', 'Vendor PO Not Required', 'bool'],
    ],
  },
  // The "Site Contacts" block — technician / FSE / AOM / contact person and
  // their phone numbers — was removed on purpose. This report is about how a
  // site moves between workflow stages; those are third-party contact details
  // that nothing here acts on. They remain on the site record itself.
  {
    title: 'Field Work',
    fields: [
      ['team_coordinator', 'Team Coordinator'],
      ['site_engineer', 'Site Engineer'],
      ['site_engineer_code', 'Employee Code'],
      ['site_engineer_association', 'Association'],
      ['site_engineer_assign_date', 'Assign Date', 'date'],
      ['site_engineer_assign_time', 'Assign Time'],
      ['engineer_assigned_at', 'Engineer Assigned On (Log)', 'date'],
      ['field_work_start_date', 'Field Work Start Date', 'date'],
      ['site_unlock_latitude', 'Unlock Latitude'],
      ['site_unlock_longitude', 'Unlock Longitude'],
      ['site_engineer_work_date', 'Work Date', 'date'],
      ['field_work_completed_date', 'Field Work Completed', 'date'],
      ['field_work_duration_days', 'Duration (Days)'],
      ['site_engineer_work_status', 'Work Status'],
      ['site_engineer_visit_status', 'Visit Status'],
      ['site_engineer_work_remarks', 'Work Remarks'],
      ['visit_remarks', 'Visit Remarks'],
      ['is_location_verified', 'Location Verified', 'bool'],
      ['location_verification_attempts', 'Verification Attempts'],
      ['last_location_check', 'Last Location Check', 'date'],
    ],
  },
  {
    title: 'TA Review',
    fields: [
      ['ta_name', 'TA'],
      ['ta_code', 'Employee Code'],
      ['ta_work_date', 'Work Date', 'date'],
      ['ta_review_completed_at', 'Review Completed On', 'date'],
      ['ta_rejected_at', 'Rejected On', 'date'],
      ['ta_remarks', 'Remarks'],
      ['ta_quality_feedback', 'Quality Feedback'],
      ['ta_rating', 'Rating'],
    ],
  },
  {
    title: 'MIS',
    fields: [
      ['mis_name', 'MIS'],
      ['mis_code', 'Employee Code'],
      ['mis_submission_date', 'Submission Date', 'date'],
      ['mis_remarks', 'Remarks'],
      ['mis_quality_feedback', 'Quality Feedback'],
    ],
  },
  {
    title: 'Report Designer',
    fields: [
      ['report_designer_name', 'Report Designer'],
      ['report_designer_code', 'Employee Code'],
      ['report_maker_assigned_at', 'Assigned On', 'date'],
      ['report_designer_work_date', 'Work Date', 'date'],
      ['report_completed_at', 'Report Completed On', 'date'],
      ['report_designer_remarks', 'Remarks'],
      ['report_designer_quality_feedback', 'Quality Feedback'],
      ['report_designer_rejection_reason', 'Rejection Reason'],
      ['report_maker_priority', 'Priority'],
    ],
  },
  {
    title: 'Validator',
    fields: [
      ['validator_name', 'Validator'],
      ['validator_code', 'Employee Code'],
      ['validator_assigned_at', 'Assigned On', 'date'],
      ['validator_work_date', 'Work Date', 'date'],
      ['validation_completed_at', 'Validation Completed On', 'date'],
      ['validator_remarks', 'Remarks'],
      ['validator_quality_feedback', 'Quality Feedback'],
      ['validator_rating', 'Rating'],
      ['validator_priority', 'Priority'],
    ],
  },
  {
    title: 'Delivery & Data',
    fields: [
      ['report_finalized_at', 'Report Finalized On', 'date'],
      ['report_sent_to_client_at', 'Sent To Client On', 'date'],
      ['total_tat_days', 'Total TAT (Days)'],
      ['uploaded_files_count', 'Uploaded Files'],
      ['uploaded_images_count', 'Images'],
      ['uploaded_videos_count', 'Videos'],
      ['uploaded_documents_count', 'Documents'],
      ['uploaded_size', 'Total Data Size'],
      ['first_upload_at', 'First Upload On', 'date'],
      ['last_upload_at', 'Last Upload On', 'date'],
      ['hold_reason', 'Hold Reason'],
      ['cancel_reason', 'Cancel Reason'],
      ['created_by_name', 'Created By'],
      ['created_at', 'Created At', 'date'],
      ['updated_at', 'Updated At', 'date'],
    ],
  },
];

interface DetailModalProps {
  row: SurveyWorkflowRow | null;
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ row, onClose }) => {
  if (!row) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded shadow-lg w-full max-w-6xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {row.site_id} — {row.site_name || 'Unnamed site'}
            </h2>
            <p className="text-sm text-gray-600">
              {row.project_name || 'NA'} · Stage: {row.stage_label}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-800" aria-label="Close">
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {DETAIL_SECTIONS.map((section) => (
            <section key={section.title}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{section.title}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                {section.fields.map(([key, label, type]) => (
                  <div key={key} className="text-sm">
                    <span className="text-gray-500">{label}: </span>
                    <span className="text-gray-900 break-words">{display(row[key], type)}</span>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Workflow Trail ({(row.workflow_logs || []).length} steps)
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">#</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">From</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">To</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Action</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Performed On</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Performed By</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(row.workflow_logs || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">No workflow steps recorded.</td>
                    </tr>
                  ) : (
                    (row.workflow_logs || []).map((log: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                        <td className="px-3 py-2">{display(log.from_status_label)}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">{display(log.to_status_label)}</td>
                        <td className="px-3 py-2">{display(log.action_type)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(log.performed_at)}</td>
                        <td className="px-3 py-2">{display(log.performed_by)}</td>
                        <td className="px-3 py-2 max-w-xs break-words">{display(log.remarks)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {(row.rejections || []).length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Rejections ({row.rejections.length})
              </h3>
              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Stage</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Reason</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Rejected On</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Rejected By</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Resolved</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {row.rejections.map((rejection: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{display(rejection.rejection_stage_label)}</td>
                        <td className="px-3 py-2">{display(rejection.rejection_type)}</td>
                        <td className="px-3 py-2 max-w-xs break-words">{display(rejection.rejection_reason)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(rejection.rejected_at)}</td>
                        <td className="px-3 py-2">{display(rejection.rejected_by)}</td>
                        <td className="px-3 py-2">{rejection.is_resolved ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SurveyWorkflowReport: React.FC = () => {
  const [stageOptions, setStageOptions] = useState<StageOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [activityOptions, setActivityOptions] = useState<SelectOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedRole, setSelectedRole] = useState<SelectOption | null>(null);

  const [draftFilters, setDraftFilters] = useState<SurveyWorkflowFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<SurveyWorkflowFilters>(DEFAULT_FILTERS);
  const [showFilterForm, setShowFilterForm] = useState(true);

  const [rows, setRows] = useState<SurveyWorkflowRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [exporting, setExporting] = useState(false);
  /** Bytes of the streamed export received so far — the only progress the
   * server can report, since the row count is not known until it ends. */
  const [exportedBytes, setExportedBytes] = useState(0);
  const [detailRow, setDetailRow] = useState<SurveyWorkflowRow | null>(null);

  // 'list' = the site-by-site table; 'summary' = the stage x project matrix.
  const [viewMode, setViewMode] = useState<'list' | 'summary'>('list');

  // Stage, project and department dropdowns load once; all are scoped
  // server-side. They are loaded INDEPENDENTLY: a single failing endpoint used
  // to reject the whole Promise.all and leave the form with an empty Workflow
  // Stage dropdown, which made the page look broken and said nothing about
  // which call actually failed. Each source now fails on its own, naming
  // itself, and the rest of the form still fills in.
  useEffect(() => {
    const loadFrom = async <T,>(
      path: string,
      label: string,
      pick: (data: any) => T,
      apply: (value: T) => void,
    ) => {
      try {
        const response = await axiosInstance.get(path);
        if (!response.data?.success) throw new Error(response.data?.message || label);
        apply(pick(response.data.data || {}));
      } catch (error: any) {
        console.error(`[survey-workflow] ${path} failed:`, error);
        // Same id per source, so React StrictMode's double-invoked effect (and
        // a later retry) collapses into one toast instead of stacking.
        toast.error(
          error?.response?.status === 404
            ? `${label} unavailable — the server is running an older build.`
            : error?.response?.data?.message || `Failed to load ${label.toLowerCase()}`,
          { id: `swf-filter-${path}` },
        );
      }
    };

    loadFrom(
      '/reports/survey-workflow/stages',
      'Workflow stages',
      (data) => (data.stages || []) as StageOption[],
      setStageOptions,
    );
    loadFrom(
      '/reports/survey-workflow/projects',
      'Projects',
      (data) =>
        (data.projects || []).map((project: any) => ({
          id: project.id,
          value: project.id,
          label: project.project_short_name
            ? `${project.project_name} (${project.project_short_name})`
            : project.project_name,
        })) as SelectOption[],
      setProjectOptions,
    );
    loadFrom(
      '/reports/survey-workflow/departments',
      'Departments',
      (data) =>
        (data.departments || []).map((department: DepartmentOption) => ({
          id: department.id,
          value: department.id,
          label: department.name,
        })) as SelectOption[],
      setDepartmentOptions,
    );
    loadFrom(
      '/reports/survey-workflow/activities',
      'Activities',
      (data) =>
        (data.activities || []).map((activity: any) => ({
          id: activity.id,
          value: activity.id,
          label: activity.activity_name,
        })) as SelectOption[],
      setActivityOptions,
    );
    loadFrom(
      '/reports/survey-workflow/roles',
      'Roles',
      (data) =>
        (data.roles || []).map((role: any) => ({
          id: role.id,
          value: role.id,
          label: role.name,
        })) as SelectOption[],
      setRoleOptions,
    );
  }, []);

  // Users are always fetched within the caller's own projects; picking
  // projects narrows them further to those projects' members. `projectIds` is
  // a CSV list, so one project and several are the same call.
  const loadUsers = useCallback(async (projectIds: string): Promise<SelectOption[]> => {
    try {
      const response = await axiosInstance.get('/reports/survey-workflow/users', {
        params: projectIds ? { project_id: projectIds } : undefined,
      });
      if (!response.data?.success) return [];
      return (response.data.data?.users || []).map((user: any) => ({
        id: user.id,
        value: user.id,
        label: [user.name || user.username, user.employee_code ? `(${user.employee_code})` : null, user.designation ? `- ${user.designation}` : null]
          .filter(Boolean)
          .join(' '),
      }));
    } catch (error: any) {
      console.error('[survey-workflow] /reports/survey-workflow/users failed:', error);
      // Fixed id: both the List and the Detailed engineer lists load from here,
      // and StrictMode invokes each effect twice — without this the same
      // failure stacks up as four identical toasts.
      toast.error(
        error?.response?.status === 404
          ? 'User list unavailable — the server is running an older build.'
          : error?.response?.data?.message || 'Failed to load users',
        { id: 'swf-filter-users' },
      );
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadUsers(draftFilters.project_id).then((options) => {
      if (!cancelled) setUserOptions(options);
    });
    return () => {
      cancelled = true;
    };
  }, [draftFilters.project_id, loadUsers]);

  const buildParams = (page: number, limit: number, filters: SurveyWorkflowFilters) => {
    const params: Record<string, string | number> = { page, limit };
    if (filters.workflow_stage) params.workflow_stage = filters.workflow_stage;
    if (filters.stage_match) params.stage_match = filters.stage_match;
    // CSV — the List view takes several projects at once.
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.user_id) params.user_id = Number(filters.user_id);
    if (filters.department_id) params.department_id = Number(filters.department_id);
    if (filters.activity_id) params.activity_id = Number(filters.activity_id);
    if (filters.role_id) params.role_id = Number(filters.role_id);
    if (filters.stage_date_from) params.stage_date_from = filters.stage_date_from;
    if (filters.stage_date_to) params.stage_date_to = filters.stage_date_to;
    // Always sent: the server's own default matches, but sending it keeps the
    // request self-describing and the Excel export in step with the table.
    params.completion_status =
      filters.completion_status || DEFAULT_FILTERS.completion_status;
    if (filters.site_id) params.site_id = filters.site_id;
    if (filters.search) params.search = filters.search;
    if (filters.sort_by) params.sort_by = filters.sort_by;
    if (filters.sort_order) params.sort_order = filters.sort_order;
    return params;
  };

  const fetchRows = async (
    targetPage = 1,
    targetLimit = pagination.items_per_page,
    filters: SurveyWorkflowFilters = appliedFilters,
  ) => {
    if (!filters.workflow_stage) {
      toast.error('Select a workflow stage first');
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/survey-workflow', {
        params: buildParams(targetPage, targetLimit, filters),
      });
      if (!response.data?.success) return;

      const payload = response.data.data || {};
      const data: SurveyWorkflowRow[] = payload.data || [];
      const meta = payload.pagination || {};
      const total = Number(meta.total ?? data.length) || 0;
      const currentPage = Number(meta.current_page ?? targetPage) || targetPage;
      const perPage = Number(meta.per_page ?? targetLimit) || targetLimit;
      const totalPages = Number(meta.total_pages) || Math.max(1, Math.ceil(total / perPage));

      setRows(data);
      setPagination({
        total_items: total,
        current_page: currentPage,
        total_pages: totalPages,
        items_per_page: perPage,
        has_next_page: Boolean(meta.has_next_page ?? currentPage < totalPages),
        has_previous_page: Boolean(meta.has_prev_page ?? currentPage > 1),
      });
      setHasFetched(true);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to fetch survey workflow report');
    } finally {
      setLoading(false);
    }
  };

  // The server builds the workbook so the export carries every column,
  // including one "reached on" column per workflow stage.
  //
  // It arrives as a stream: on a full-project run (17k+ sites) the file is
  // written batch by batch and can take minutes, so the shared 2-minute axios
  // timeout has to be lifted for this one request. Bytes arrive throughout, so
  // the download progress is real and worth showing.
  const handleExport = async () => {
    if (!appliedFilters.workflow_stage) {
      toast.error('Apply a workflow stage before exporting');
      return;
    }
    try {
      setExporting(true);
      setExportedBytes(0);
      const response = await axiosInstance.get('/reports/survey-workflow/export', {
        params: buildParams(1, pagination.items_per_page, appliedFilters),
        responseType: 'blob',
        timeout: EXPORT_TIMEOUT_MS,
        onDownloadProgress: (event) => setExportedBytes(event.loaded || 0),
      });

      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match ? decodeURIComponent(match[1]) : 'survey-workflow-report.xlsx';

      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (error: any) {
      let message = 'Failed to export survey workflow report';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.message || message;
        } catch {
          /* not JSON — keep the generic message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
      setExportedBytes(0);
    }
  };

  const paginationAdapter: PaginationType = useMemo(
    () => ({
      offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
      limit: pagination.items_per_page,
      page: pagination.current_page,
      total_pages: pagination.total_pages,
      total_count: pagination.total_items,
      has_next_page: pagination.has_next_page,
      has_prev_page: pagination.has_previous_page,
      next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
      prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
    }),
    [pagination],
  );

  const activeFiltersCount = useMemo(
    () =>
      Object.entries(appliedFilters).filter(([key, value]) => {
        if (!value) return false;
        if (key === 'sort_by' && value === DEFAULT_FILTERS.sort_by) return false;
        if (key === 'sort_order' && value === DEFAULT_FILTERS.sort_order) return false;
        if (key === 'stage_match' && value === DEFAULT_FILTERS.stage_match) return false;
        if (key === 'completion_status' && value === DEFAULT_FILTERS.completion_status)
          return false;
        return true;
      }).length,
    [appliedFilters],
  );

  const handleFilterChange = (key: keyof SurveyWorkflowFilters, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    if (!draftFilters.workflow_stage) {
      toast.error('Workflow stage is required');
      return;
    }
    const dateError = dateRangeError(draftFilters.stage_date_from, draftFilters.stage_date_to, 'Stage Date');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setAppliedFilters(draftFilters);
    setShowFilterForm(false);
    fetchRows(1, pagination.items_per_page, draftFilters);
  };

  const handleResetDraft = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setSelectedProjects([]);
    setSelectedUser(null);
    setSelectedDepartment(null);
    setSelectedActivity(null);
    setSelectedRole(null);
  };

  const handleClearAll = () => {
    handleResetDraft();
    setAppliedFilters(DEFAULT_FILTERS);
    setRows([]);
    setHasFetched(false);
    setShowFilterForm(true);
    setPagination(EMPTY_PAGINATION);
  };

  const appliedStageLabel =
    stageOptions.find((stage) => stage.value === appliedFilters.workflow_stage)?.label || '';

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Survey Workflow Report</h1>
            <p className="text-sm text-gray-600 mt-1">
              {viewMode === 'list'
                ? 'Stage-wise site detail across the survey workflow — restricted to your assigned projects.'
                : 'How many sites sit at each workflow stage, per project — restricted to your assigned projects.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle: the site-by-site list vs. the stage x project counts */}
            <div className="flex rounded border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                title="List — one row per site, for a single workflow stage"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${viewMode === 'list' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaListUl className="w-4 h-4" /> List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('summary')}
                aria-pressed={viewMode === 'summary'}
                title="Summary — site counts per workflow stage, one column per project"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-gray-200 ${viewMode === 'summary' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaTable className="w-4 h-4" /> Summary
              </button>
            </div>

            {viewMode === 'list' ? (
              <>
                {!showFilterForm && (
                  <button
                    onClick={() => setShowFilterForm(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    Apply Filters Again
                    {activeFiltersCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                )}
                <button
                  onClick={handleClearAll}
                  className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  onClick={handleExport}
                  disabled={!hasFetched || exporting}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                >
                  {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
                  {exporting
                    ? exportedBytes > 0
                      ? `Downloading ${formatBytes(exportedBytes)}...`
                      : 'Preparing...'
                    : 'Export Excel'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {viewMode === 'summary' && <SurveyWorkflowSummary />}

      {viewMode === 'list' && showFilterForm && (
        <div className="bg-white shadow-sm rounded border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Survey Workflow Report Filters</h2>
            <p className="text-sm text-gray-600">
              Workflow stage is mandatory. User and project are optional — the project list holds only your
              assigned projects, and the user list only people who share a project with you.
            </p>
          </div>

          <div className="p-4 space-y-6">
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Stage</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    Workflow Stage <span className="text-red-600">*</span>
                  </span>
                  <select
                    value={draftFilters.workflow_stage}
                    onChange={(event) => handleFilterChange('workflow_stage', event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="">Select workflow stage</option>
                    {stageOptions.map((stage) => (
                      <option key={stage.value} value={stage.value}>
                        {stage.sequence}. {stage.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Stage Match</span>
                  <select
                    value={draftFilters.stage_match}
                    onChange={(event) => handleFilterChange('stage_match', event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="current">Currently at this stage</option>
                    <option value="reached">Reached this stage (ever)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Stage Date From</span>
                  <input
                    type="date"
                    value={draftFilters.stage_date_from}
                    onChange={(event) => handleFilterChange('stage_date_from', event.target.value)}
                    {...dateRangeInputBounds(draftFilters.stage_date_from, draftFilters.stage_date_to).from}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Stage Date To</span>
                  <input
                    type="date"
                    value={draftFilters.stage_date_to}
                    onChange={(event) => handleFilterChange('stage_date_to', event.target.value)}
                    {...dateRangeInputBounds(draftFilters.stage_date_from, draftFilters.stage_date_to).to}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </label>
                <div className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    Work Completion Status
                  </span>
                  <CompletionStatusFilter
                    value={draftFilters.completion_status}
                    onChange={(value) => handleFilterChange('completion_status', value)}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Project, User & Scope</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Project (your projects only)</span>
                  <SearchableSelect
                    placeholder="All my projects"
                    value={selectedProjects}
                    options={projectOptions}
                    isMulti
                    showSelectAllVisible
                    onChange={(options: SelectOption[] | SelectOption | null) => {
                      const next = Array.isArray(options) ? options : options ? [options] : [];
                      setSelectedProjects(next);
                      // The user list is scoped to the picked projects, so a
                      // user chosen under the old selection may no longer be
                      // in it — drop it rather than filter on a stale id.
                      setSelectedUser(null);
                      setDraftFilters((prev) => ({
                        ...prev,
                        project_id: next.map((option) => option.value).join(','),
                        user_id: '',
                      }));
                    }}
                    className="w-full"
                  />
                </div>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">User (from your projects)</span>
                  <SelectWithSearch
                    placeholder="All users"
                    value={selectedUser}
                    options={userOptions}
                    onChange={(option) => {
                      setSelectedUser(option);
                      handleFilterChange('user_id', option ? String(option.value) : '');
                    }}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    Department / Project Group
                  </span>
                  <SelectWithSearch
                    placeholder="All departments"
                    value={selectedDepartment}
                    options={departmentOptions}
                    onChange={(option) => {
                      setSelectedDepartment(option);
                      handleFilterChange('department_id', option ? String(option.value) : '');
                    }}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Activity</span>
                  <SelectWithSearch
                    placeholder="All activities"
                    value={selectedActivity}
                    options={activityOptions}
                    onChange={(option) => {
                      setSelectedActivity(option);
                      handleFilterChange('activity_id', option ? String(option.value) : '');
                    }}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Role</span>
                  <SelectWithSearch
                    placeholder="All roles"
                    value={selectedRole}
                    options={roleOptions}
                    onChange={(option) => {
                      setSelectedRole(option);
                      handleFilterChange('role_id', option ? String(option.value) : '');
                    }}
                    className="w-full"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Site ID</span>
                  <input
                    type="text"
                    value={draftFilters.site_id}
                    onChange={(event) => handleFilterChange('site_id', event.target.value)}
                    placeholder="SIT"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Search</span>
                  <input
                    type="text"
                    value={draftFilters.search}
                    onChange={(event) => handleFilterChange('search', event.target.value)}
                    placeholder="Site ID, name or reference"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </label>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Sorting</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Sort By</span>
                  <select
                    value={draftFilters.sort_by}
                    onChange={(event) => handleFilterChange('sort_by', event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">Sort Order</span>
                  <select
                    value={draftFilters.sort_order}
                    onChange={(event) => handleFilterChange('sort_order', event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="DESC">DESC</option>
                    <option value="ASC">ASC</option>
                  </select>
                </label>
              </div>
            </section>
          </div>

          <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
            <button
              onClick={handleResetDraft}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              onClick={handleApplyFilters}
              disabled={!draftFilters.workflow_stage}
              className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {viewMode === 'list' && !showFilterForm && (
        <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-700">
              Stage: <span className="font-semibold">{appliedStageLabel || appliedFilters.workflow_stage}</span>
              <span className="text-gray-400"> · </span>
              {appliedFilters.stage_match === 'current' ? 'Currently at stage' : 'Reached stage'}
            </p>
            <p className="text-sm text-gray-500">{pagination.total_items} site(s)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {TABLE_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                    >
                      {column.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length + 1} className="px-4 py-10 text-center text-sm text-gray-600">
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length + 1} className="px-4 py-10 text-center text-sm text-gray-600">
                      No sites found for this stage.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id ?? index} className="hover:bg-gray-50">
                      {TABLE_COLUMNS.map((column) => (
                        <td
                          key={column.key}
                          className="px-4 py-3 text-sm text-gray-700 align-top max-w-xs whitespace-normal break-words"
                        >
                          {display(row[column.key], column.type)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-sm align-top">
                        <button
                          onClick={() => setDetailRow(row)}
                          className="text-blue-700 hover:text-blue-900 font-medium whitespace-nowrap"
                        >
                          View all
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {hasFetched && (
            <Pagination
              pagination={paginationAdapter}
              onPageChange={(nextPage) => fetchRows(nextPage, pagination.items_per_page, appliedFilters)}
              onLimitChange={(nextLimit) => fetchRows(1, nextLimit, appliedFilters)}
            />
          )}
        </div>
      )}

      {viewMode === 'list' && showFilterForm && !hasFetched && (
        <div className="bg-white border border-gray-200 rounded flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">Select a workflow stage to view the report.</p>
          <p className="text-xs text-gray-400 mt-1">User and project filters are optional.</p>
        </div>
      )}

      <DetailModal row={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
};

export default SurveyWorkflowReport;
